import DayJS from "dayjs";
import utc from "dayjs/plugin/utc";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Button from "@/components/common/Button";
import { getDayOfWeek, getHoursAndMinutes, getMonthName } from "./Helper";

DayJS.extend(utc);

const RNBooking = ({
  onDateChange,
  onDayTimePress,
  onDayPress = null,
  renderInsideBox,
  bookingData,
  scrollToCurrentDate = true,
}) => {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const currentMonth = selectedDate.getMonth();
  const currentYear = selectedDate.getFullYear();

  const getDaysInMonth = useCallback(
    (month, year) => new Date(year, month, 0).getDate(),
    []
  );
  const generateCalendar = useCallback(
    (month, year) => {
      const daysInMonth = getDaysInMonth(month, year);
      const days = [];
      for (let i = 1; i <= daysInMonth; i++) {
        days.push(i);
      }
      return days;
    },
    [getDaysInMonth]
  );
  const currentMonthName = useMemo(
    () => getMonthName(currentMonth),
    [currentMonth]
  );
  const allSlots = useMemo(
    () => [currentMonthName, ...bookingData.slots],
    [currentMonthName, bookingData.slots]
  );
  const calendar = useMemo(
    () => generateCalendar(currentMonth + 1, currentYear),
    [currentMonth, currentYear, generateCalendar]
  );
  const handleMonthChange = useCallback(
    (direction) => {
      let newDate = DayJS(selectedDate).startOf("month");

      if (direction >= 1) newDate = newDate.add(1, "month");
      if (direction <= -1) newDate = newDate.subtract(1, "month");

      setSelectedDate(newDate.toDate());
      onDateChange?.(newDate.toDate());
    },
    [selectedDate, onDateChange]
  );
  const getDay = useMemo(
    () => (day) => {
      return getDayOfWeek(currentMonth, day, currentYear);
    },
    [currentMonth, currentYear]
  );
  const handleDayTimePress = useCallback(
    (day, time, shift) => {
      const { hours, minutes } = getHoursAndMinutes(time);
      const date = DayJS(selectedDate)
        // .utc(true)
        .set("date", day)
        .startOf("day")
        .set("hours", hours)
        .set("minutes", minutes)
        .set("seconds", 0);
      console.log("aa", date.toISOString());
      console.log("bb", date.unix());

      onDayTimePress?.(date.toISOString(), shift);
    },
    [selectedDate, onDayTimePress]
  );
  const handleDayPress = useCallback(
    (day) => {
      let result = [];
      const hours = Array.from({ length: 24 }, (_, i) => i); // Generate an array of hours (0 to 23)
      hours.forEach((hour) => {
        const date = new Date(selectedDate);
        date.setDate(day);
        date.setHours(hour, 0, 0, 0);
        result.push(date.toISOString());
      });
      if (onDayPress !== null) onDayPress(result);
    },
    [selectedDate, onDayPress]
  );
  const checkIfToday = useMemo(
    () => (day) => {
      const calendarDate = new Date(selectedDate);
      calendarDate.setDate(day);
      const todayDate = new Date();
      return (
        todayDate.setHours(0, 0, 0, 0) === calendarDate.setHours(0, 0, 0, 0)
      );
    },
    [selectedDate]
  );
  const getDayWiseBookingInfo = useMemo(
    () => (day) => {
      const selectedSlot = DayJS(selectedDate)
        .utc(true)
        .set("date", day)
        .startOf("day");
      const date = selectedSlot.toDate();

      const dayWiseBookedSlots =
        bookingData?.bookedSlots?.find((b) => {
          const bookingDate = new Date(parseInt(b.date, 10));
          bookingDate.setHours(0, 0, 0, 0);
          return bookingDate.toDateString() === date.toDateString();
        })?.slots ?? [];
      const dayWiseAvailableSlots =
        bookingData?.availableSlots?.find((b) => {
          const bookingDate = new Date(parseInt(b.date, 10));
          bookingDate.setHours(0, 0, 0, 0);
          return bookingDate.toDateString() === date.toDateString();
        })?.slots ?? [];
      return {
        availableSlots: dayWiseAvailableSlots,
        bookedSlots: dayWiseBookedSlots,
      };
    },
    [
      bookingData?.availableSlots,
      bookingData?.bookedSlots,
      currentMonth,
      currentYear,
    ]
  );

  const container = useRef(null);
  const containerNav = useRef(null);
  const containerOffset = useRef(null);

  useEffect(() => {
    if (scrollToCurrentDate) {
      // Set the container scroll position based on the current time.
      const currentMinute = new Date().getHours() * 60;
      container.current.scrollTop =
        ((container.current.scrollHeight -
          containerNav.current.offsetHeight -
          containerOffset.current.offsetHeight) *
          currentMinute) /
        1440;
    }
  }, [scrollToCurrentDate]);

  const getBoxBorder = (dayNum, hourSlot) => {
    if (bookingData.shiftData !== undefined) {
      const theHour = hourSlot.split(":");
      const selectedSlot = DayJS(selectedDate)
        .set("date", dayNum)
        .set("hour", theHour[0]);
      const theShift = bookingData.shiftData.filter((item) => {
        const start = DayJS.utc(item.startAt);
        const end = DayJS.utc(item.endAt);

        return (
          (selectedSlot.isAfter(start) || selectedSlot.isSame(start)) &&
          (selectedSlot.isBefore(end) || selectedSlot.isSame(end))
        );
      });
      if (theShift[0] !== undefined) {
        return {
          borderColor: { border: "1px solid " + theShift[0].colour },
          shift: theShift[0],
        };
      }
    }

    return {
      borderColor: {},
    };
  };

  return (
    <div className="flex h-[80vh] flex-col">
      <div className="flex justify-between border-b border-gray-700 py-2">
        <Button
          variant="outline"
          onClick={() => handleMonthChange(-1)}
          text={"Prev"}
        />
        <div>
          {`${selectedDate.toLocaleString("default", {
            month: "long",
          })}, ${currentYear}`}
        </div>
        <Button
          variant="outline"
          onClick={() => handleMonthChange(1)}
          text={"Next"}
        />
      </div>
      <div
        ref={container}
        className="isolate flex flex-auto flex-col overflow-auto"
      >
        <div className="flex max-w-full flex-none flex-col sm:max-w-none md:max-w-full">
          <div
            ref={containerNav}
            className="sticky top-0 z-[99] w-fit flex-none bg-zinc-300 dark:bg-zinc-900 sm:pr-8"
          >
            <div className="flex">
              <div className="space-x-0.5 space-y-0.5 text-center">
                <div className="m-0 ml-0.5 flex h-12 w-12 flex-col items-center justify-center border border-gray-700 bg-muted/40 text-center text-xs text-muted-foreground hover:bg-muted hover:text-white">
                  <div>{allSlots[0]}</div>
                </div>
              </div>
              {calendar.map((d, i) => {
                const isToday = checkIfToday(d);
                return (
                  <div key={i} className="space-x-0.5 space-y-0.5 text-center">
                    <div
                      onClick={() => handleDayPress(d)}
                      className={` ${
                        isToday
                          ? "bg-accent-foreground/50 text-white hover:bg-muted"
                          : "bg-muted/40 text-muted-foreground hover:bg-muted"
                      }   ml-0.5 flex h-12 w-12 flex-col items-center justify-center border border-gray-700 text-center text-xs ${
                        onDayPress !== null && "cursor-pointer"
                      } hover:text-white`}
                      id={isToday ? "today" : ""}
                    >
                      <div>{getDay(d)}</div>
                      <div>{d}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="flex flex-auto">
            <div className="mt-px grid flex-auto grid-cols-1 grid-rows-1">
              {/* Horizontal lines */}
              <div className="col-start-1 col-end-2 row-start-1 grid divide-y">
                <div ref={containerOffset} className="hidden"></div>
                <div className="sticky left-[1px] z-50 w-fit space-y-0.5  !border-0 pt-[2px] text-xs">
                  {allSlots.map((time, index) => {
                    if (index === 0) return;
                    return (
                      <div
                        key={time}
                        className={`${
                          index === 0 && "text-foreground"
                        } sticky left-0 z-20 flex h-12 w-12 items-center justify-center border border-gray-700 bg-muted align-middle`}
                      >
                        {time}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="col-start-1 col-end-2 row-start-1 ml-12 flex w-fit pl-px">
                {calendar.map((d, i) => {
                  const { bookedSlots } = getDayWiseBookingInfo(d);
                  return (
                    <div
                      key={i}
                      className="relative space-x-0.5 space-y-0.5 text-center"
                    >
                      <div />
                      {allSlots.map((time, idx) => {
                        if (idx === 0) return null;

                        const shiftData = getBoxBorder(d, time);
                        // console.log(shiftData);

                        const isBooked = bookedSlots?.includes(time) ?? false;
                        if (isBooked) {
                          return (
                            <div
                              onClick={() =>
                                handleDayTimePress(d, time, shiftData)
                              }
                              key={`booked_${d}_${time}`}
                              className={`cursor-pointer ${
                                shiftData?.shift?.id >= 0
                                  ? "bg-green-500/20"
                                  : "bg-red-500/20"
                              } h-12 w-12`}
                              style={shiftData.borderColor}
                            >
                              {renderInsideBox !== undefined
                                ? renderInsideBox(d, time, shiftData)
                                : ""}
                            </div>
                          );
                        }
                        return (
                          <div
                            onClick={() =>
                              handleDayTimePress(d, time, shiftData)
                            }
                            key={`${d}_${time}`}
                            className={`h-12 w-12 cursor-pointer bg-muted hover:bg-muted-foreground`}
                            style={shiftData.borderColor}
                          >
                            {renderInsideBox !== undefined
                              ? renderInsideBox(d, time, shiftData)
                              : ""}
                          </div>
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RNBooking;
